const mockData = {
  companyName: "Acme Corporation",
  createdAt: "2024-03-15T09:32:00Z",
  superAdmin: {
    name: "Jordan Mitchell",
    email: "jordan.mitchell@acme.com",
  },
  logo: null,
  isOverLimit: {
    users: true,
    mappings: false,
  },
  overLimitSince: "2024-11-20T00:00:00Z",
  subscription: {
    plan: "basic",
    status: "active",
    startedAt: "2024-03-15T00:00:00Z",
    expiresAt: "2025-03-15T00:00:00Z",
    usage: {
      usersCount: 3,
      savedMappings: 4,
      executionCount: 187,
    },
    limits: {
      maxUsers: 2,
      maxSavedMappings: 10,
      maxExecutionsPerMonth: 200,
      canSaveMappings: true,
    },
  },
};

const fmt = (iso) => {
  if (!iso) return "—";
  return new Date(iso).toLocaleDateString("en-US", {
    year: "numeric",
    month: "long",
    day: "numeric",
  });
};

const fmtTime = (iso) => {
  if (!iso) return "—";
  return new Date(iso).toLocaleString("en-US", {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const planConfig = {
  free: {
    bg: "bg-slate-100",
    text: "text-slate-600",
    border: "border-slate-200",
    dot: "bg-slate-400",
  },
  basic: {
    bg: "bg-teal-50",
    text: "text-teal-700",
    border: "border-teal-200",
    dot: "bg-teal-400",
  },
  premium: {
    bg: "bg-amber-50",
    text: "text-amber-700",
    border: "border-amber-200",
    dot: "bg-amber-400",
  },
};

const statusConfig = {
  active: {
    bg: "bg-emerald-50",
    text: "text-emerald-700",
    border: "border-emerald-200",
    dot: "bg-emerald-400",
  },
  cancelled: {
    bg: "bg-rose-50",
    text: "text-rose-600",
    border: "border-rose-200",
    dot: "bg-rose-400",
  },
  expired: {
    bg: "bg-gray-100",
    text: "text-gray-500",
    border: "border-gray-200",
    dot: "bg-gray-400",
  },
};

function UsageBar({ label, used, max, overLimit }) {
  const unlimited = max === 0;
  const pct = unlimited ? 0 : Math.min((used / max) * 100, 100);
  const isOver = overLimit || (!unlimited && used > max);
  const barColor = isOver
    ? "bg-rose-400"
    : pct > 75
      ? "bg-amber-400"
      : "bg-teal-400";

  return (
    <div className="mb-5">
      <div className="flex justify-between items-center mb-2">
        <span className="text-xs text-stone-400 tracking-wide">{label}</span>
        <span
          className={`text-xs font-semibold font-mono ${isOver ? "text-rose-500" : "text-stone-500"}`}
        >
          {used.toLocaleString()} / {unlimited ? "∞" : max.toLocaleString()}
        </span>
      </div>
      <div className="w-full h-1.5 rounded-full bg-stone-100 overflow-hidden">
        {!unlimited ? (
          <div
            className={`h-full rounded-full transition-all duration-700 ease-out ${barColor}`}
            style={{ width: `${pct}%` }}
          />
        ) : (
          <div className="h-full rounded-full bg-stone-200 w-full" />
        )}
      </div>
    </div>
  );
}

function Badge({ children, bg, text, border, dot }) {
  return (
    <span
      className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold border ${bg} ${text} ${border}`}
    >
      {dot && <span className={`w-1.5 h-1.5 rounded-full ${dot}`} />}
      {children}
    </span>
  );
}

function Divider() {
  return (
    <div className="my-7 h-px bg-gradient-to-r from-transparent via-stone-200 to-transparent" />
  );
}

function InfoCell({ label, value }) {
  return (
    <div className="bg-stone-50 border border-stone-100 rounded-xl p-4">
      <div className="text-xs text-stone-400 tracking-widest uppercase font-mono mb-1.5">
        {label}
      </div>
      <div className="text-sm font-medium text-stone-700">{value}</div>
    </div>
  );
}

const Usage = () => {
  const d = mockData;
  const sub = d.subscription;
  const plan = planConfig[sub.plan] || planConfig.free;
  const status = statusConfig[sub.status] || statusConfig.active;
  const overUsers = d.isOverLimit.users;
  const overMappings = d.isOverLimit.mappings;
  const hasOverLimit = overUsers || overMappings;

  const initials = d.companyName
    .split(" ")
    .map((w) => w[0])
    .join("")
    .slice(0, 2)
    .toUpperCase();
  const adminInitials = d.superAdmin.name
    .split(" ")
    .map((w) => w[0])
    .join("")
    .slice(0, 2)
    .toUpperCase();

  return (
    <div className="min-h-screen bg-stone-100 flex items-start justify-center p-8">
      <div className="w-full max-w-lg">
        <div className="bg-white rounded-2xl border border-stone-200 shadow-sm overflow-hidden">
          <div className="h-0.5 bg-gradient-to-r from-teal-300 via-violet-300 to-amber-300" />
          <div className="p-8">
            <div className="text-xs text-stone-400 tracking-widest uppercase font-mono mb-5">
              Company Overview
            </div>
            <div className="flex items-start gap-4 mb-6">
              <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-teal-400 to-teal-600 flex items-center justify-center text-white text-base font-bold flex-shrink-0 shadow-sm">
                {initials}
              </div>
              <div>
                <h1 className="text-xl font-semibold text-stone-800 leading-tight">
                  {d.companyName}
                </h1>
                <p className="text-xs text-stone-400 mt-0.5">
                  Created {fmtTime(d.createdAt)}
                </p>
              </div>
            </div>

            <div className="bg-stone-50 border border-stone-100 rounded-xl p-4">
              <div className="text-xs text-stone-400 tracking-widest uppercase font-mono mb-3">
                Super Administrator
              </div>
              <div className="flex items-center gap-3">
                <div className="w-9 h-9 rounded-full bg-gradient-to-br from-violet-300 to-violet-500 flex items-center justify-center text-white text-xs font-bold flex-shrink-0">
                  {adminInitials}
                </div>
                <div>
                  <div className="text-sm font-semibold text-stone-700">
                    {d.superAdmin.name}
                  </div>
                  <div className="text-xs text-stone-400 font-mono">
                    {d.superAdmin.email}
                  </div>
                </div>
              </div>
            </div>

            <Divider />

            <div className="text-xs text-stone-400 tracking-widest uppercase font-mono mb-5">
              Subscription
            </div>
            <div className="flex flex-wrap gap-2 mb-5">
              <Badge bg={plan.bg} text={plan.text} border={plan.border}>
                {sub.plan.charAt(0).toUpperCase() + sub.plan.slice(1)} Plan
              </Badge>
              <Badge
                bg={status.bg}
                text={status.text}
                border={status.border}
                dot={status.dot}
              >
                {sub.status.charAt(0).toUpperCase() + sub.status.slice(1)}
              </Badge>
              {sub.limits.canSaveMappings && (
                <Badge
                  bg="bg-blue-50"
                  text="text-blue-600"
                  border="border-blue-200"
                >
                  Mappings Enabled
                </Badge>
              )}
            </div>
            <div className="grid grid-cols-2 gap-3">
              <InfoCell label="Started" value={fmt(sub.startedAt)} />
              <InfoCell label="Expires" value={fmt(sub.expiresAt)} />
            </div>

            <Divider />

            <div className="text-xs text-stone-400 tracking-widest uppercase font-mono mb-5">
              Usage & Limits
            </div>
            <UsageBar
              label="Users"
              used={sub.usage.usersCount}
              max={sub.limits.maxUsers}
              overLimit={overUsers}
            />
            <UsageBar
              label="Saved Mappings"
              used={sub.usage.savedMappings}
              max={sub.limits.maxSavedMappings}
              overLimit={overMappings}
            />
            <UsageBar
              label="Executions this month"
              used={sub.usage.executionCount}
              max={sub.limits.maxExecutionsPerMonth}
              overLimit={false}
            />

            {hasOverLimit && (
              <>
                <Divider />
                <div className="text-xs text-stone-400 tracking-widest uppercase font-mono mb-4">
                  Limit Alerts
                </div>
                <div className="space-y-3">
                  {overUsers && (
                    <div className="flex gap-3 items-start bg-rose-50 border border-rose-100 rounded-xl p-4">
                      <div className="text-rose-400 text-lg leading-none mt-0.5">
                        ⚠
                      </div>
                      <div>
                        <div className="text-sm font-semibold text-rose-600 mb-1">
                          User Limit Exceeded
                        </div>
                        <div className="text-xs text-rose-400 leading-relaxed">
                          This company has exceeded the maximum allowed users
                          {d.overLimitSince
                            ? ` since ${fmt(d.overLimitSince)}`
                            : ""}
                          . Consider upgrading their plan.
                        </div>
                      </div>
                    </div>
                  )}
                  {overMappings && (
                    <div className="flex gap-3 items-start bg-rose-50 border border-rose-100 rounded-xl p-4">
                      <div className="text-rose-400 text-lg leading-none mt-0.5">
                        ⚠
                      </div>
                      <div>
                        <div className="text-sm font-semibold text-rose-600 mb-1">
                          Mapping Limit Exceeded
                        </div>
                        <div className="text-xs text-rose-400 leading-relaxed">
                          Saved mappings have exceeded the plan's allowed
                          maximum
                          {d.overLimitSince
                            ? ` since ${fmt(d.overLimitSince)}`
                            : ""}
                          .
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </>
            )}
          </div>
        </div>
        <p className="text-center text-xs text-stone-400 mt-4 font-mono">
          Last updated · {fmtTime(new Date().toISOString())}
        </p>
      </div>
    </div>
  );
};
export default Usage;
